/**
 * REST API adapters for Auth provider
 * Transforms REST responses to match GraphQL structure
 *
 * Usage: Replace import in provider.tsx:
 * - import { useUserQuery } from './queries';
 * + import { useUserQuery } from './queries.rest';
 */
import type { User as RestUser } from '@api';
import { useCallback, useState } from 'react';

import { useCurrentUser } from '@hooks/queries';

import type { User, User_me } from './gqlTypes/User';

/**
 * Transform REST User to GraphQL User_me format
 */
const transformUserToGraphQL = (user: RestUser): User_me => ({
  __typename: 'User',
  email: user.email,
  firstName: user.first_name,
  lastName: user.last_name,
  fullName: user.full_name,
  companyName: user.company_name,
  billingAddress: user.billing_address
    ? {
        __typename: 'BillingAddress',
        email: user.billing_address.email,
        address: user.billing_address.address,
        city: user.billing_address.city,
        companyName: user.billing_address.company_name || null,
        firstName: user.billing_address.first_name || null,
        isCompany: user.billing_address.is_company,
        lastName: user.billing_address.last_name || null,
        phone: user.billing_address.phone || null,
        postalCode: user.billing_address.postal_code,
        taxId: user.billing_address.tax_id || null,
      }
    : null,
});

/**
 * REST adapter for useUserQuery
 * Matches Apollo useQuery interface with skip: true behavior
 */
export const useUserQuery = () => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const { data, isLoading, error, refetch: refetchQuery } = useCurrentUser();

  // Transform to GraphQL format
  const transformedData: User | undefined =
    shouldFetch && data
      ? {
          me: transformUserToGraphQL(data),
        }
      : undefined;

  // Refetch that returns data in Apollo format
  const refetch = useCallback(async () => {
    setShouldFetch(true);
    const result = await refetchQuery();
    if (result.data) {
      return {
        data: {
          me: transformUserToGraphQL(result.data),
        },
      };
    }
    return { data: undefined };
  }, [refetchQuery]);

  return {
    data: transformedData,
    loading: isLoading,
    error: error ? new Error(String(error)) : undefined,
    refetch,
  };
};
